import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  ArrowLeft, ArrowRight, BookOpen, CalendarDays, FileQuestion, CopyPlus,
  LibraryBig, Plus, Search, ShieldCheck, UsersRound, X,
} from "lucide-react";
import { auth, db } from "./firebaseConfig";
import QuestionImage from "./components/QuestionImage";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import {
  addOrganisationQuizToMine, listMyOrganisations, listMyQuizPublications, listOrganisationLibrary,
  loadOrganisationLibraryMedia, previewOrganisationQuiz, publishQuizToOrganisation,
} from "./utils/organisationApi";
import "./OrganisationLibrary.css";

const readableDate = (value) => {
  const seconds = value?.seconds ?? value?._seconds;
  if (!Number.isFinite(seconds)) return "Recently shared";
  return new Date(seconds * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const newAddRequestId = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const addErrorMessage = (error) => {
  switch (error?.code) {
    case "clone-unavailable": return "This test is no longer available here, or your organisation membership changed. Refresh the Shared Library.";
    case "legacy-question-image": return "This test has an older question image that cannot be copied safely. Ask its owner to replace that image.";
    case "media-not-supported": return "This test has media that cannot be copied safely.";
    case "source-changed":
    case "source-media-changed": return "The source changed while it was being copied. Refresh the preview before trying again.";
    case "code-collision": return "The new test code became unavailable. Try a new Add attempt.";
    case "code-unavailable": return "A new test code could not be issued. Try again.";
    case "clone-in-progress": return "This copy is still being prepared. Retry shortly; StudyDen will resume the same Add attempt.";
    case "request-conflict": return "This Add attempt can no longer be resumed. Try a new Add attempt.";
    case "teacher-required": return "An active teacher account is required to add this test.";
    case "unauthenticated": return "Please sign in again before adding this test.";
    case "invalid-request":
    case "invalid-request-id": return "This Add request is invalid. Refresh the page and try again.";
    case "invalid-source":
    case "noncanonical-source":
    case "owned-media-link":
    case "invalid-media-object": return "This published test cannot be copied safely.";
    case "target-media-conflict": return "The copy's media changed while it was being prepared. Try a new Add attempt.";
    case "clone-too-large": return "This test is too large to copy safely.";
    default: return "This test could not be added. Try again; StudyDen will check the same Add attempt.";
  }
};

const terminalAddCodes = new Set([
  "clone-unavailable", "legacy-question-image", "media-not-supported", "source-changed",
  "source-media-changed", "code-collision", "request-conflict", "invalid-source",
  "noncanonical-source", "owned-media-link", "invalid-media-object", "target-media-conflict",
  "clone-too-large", "teacher-required", "unauthenticated", "invalid-request", "invalid-request-id",
]);

function LearningBlocks({ blocks, mediaUrls, mediaErrors }) {
  return <div className="ol-content-blocks" aria-label="Learning Content">{blocks.map((block) => {
    if (block.type === "heading") return block.level === 3
      ? <h3 key={block.id}>{block.text}</h3> : <h2 key={block.id}>{block.text}</h2>;
    if (block.type === "text") return <p key={block.id}>{block.text}</p>;
    if (block.type === "link") return <a key={block.id} href={block.url} target="_blank" rel="noopener noreferrer">{block.label} ↗</a>;
    if (block.type === "image") return <figure key={block.id}>
      {mediaUrls[block.id] ? <img src={mediaUrls[block.id]} alt={block.altText} /> :
        <span className="ol-media-placeholder">{mediaErrors[block.id] || "Loading image…"}</span>}
      {block.caption && <figcaption>{block.caption}</figcaption>}
    </figure>;
    if (block.type === "resource") return <div key={block.id} className="ol-resource">
      <span>PDF</span><strong>{block.label}</strong>
      {mediaUrls[block.id] ? <a href={mediaUrls[block.id]} target="_blank" rel="noopener noreferrer">Open resource</a> :
        <small>{mediaErrors[block.id] || "Loading resource…"}</small>}
    </div>;
    return null;
  })}</div>;
}

function QuestionPreview({ question, number, organisationId, quizId }) {
  const options = Array.isArray(question.options) ? question.options : [];
  const correct = options.find((option) => option.id === question.correctOptionId);
  return <article className="ol-question">
    <div className="ol-question-heading"><span>Question {question.questionLabel || number}</span><strong>{question.maxMark ?? "—"} {question.maxMark === 1 ? "mark" : "marks"}</strong></div>
    {question.questionText && <p className="ol-question-text">{question.questionText}</p>}
    {(question.questionImageURL || question.questionMedia) && <QuestionImage question={question}
      quizId={quizId} context={{ type: "library", organisationId }} className="ol-question-image"
      alt={`Question ${number} illustration`} />}
    {options.length > 0 && <ol className="ol-options">{options.map((option, index) => <li key={option.id || index} className={option.id === question.correctOptionId ? "correct" : ""}>
      <span>{String.fromCharCode(65 + index)}</span>{option.text}{option.id === question.correctOptionId && <strong>Correct</strong>}
    </li>)}</ol>}
    {correct && <div className="ol-mark-scheme"><strong>Correct answer</strong><p>{correct.text}</p></div>}
    {question.markSchemeAnswer && <div className="ol-mark-scheme"><strong>Mark scheme</strong><p>{question.markSchemeAnswer}</p></div>}
  </article>;
}

function AddQuizzesDialog({ organisation, userUid, onClose, onPublished }) {
  const [quizzes, setQuizzes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [loadKey, setLoadKey] = useState(0);
  const busyRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        const [owned, published] = await Promise.all([
          getDocs(query(collection(db, "quizzes"), where("creatorUid", "==", userUid))),
          listMyQuizPublications(),
        ]);
        if (!active) return;
        const activeIds = new Set((published.publications || [])
          .filter((item) => item.organisationId === organisation.organisationId)
          .map((item) => item.quizId));
        setQuizzes(owned.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((quiz) => quiz.creatorUid === userUid && !activeIds.has(quiz.id))
          .sort((a, b) => (a.quizTitle || a.title || a.quizCode || "")
            .localeCompare(b.quizTitle || b.title || b.quizCode || "")));
      } catch (loadError) {
        if (active) setError(loadError.message || "Could not load your tests. Try again.");
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [organisation.organisationId, userUid, loadKey]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busyRef.current) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const filtered = quizzes.filter((quiz) =>
    `${quiz.quizTitle || quiz.title || ""} ${quiz.subject || ""} ${quiz.quizCode || ""}`
      .toLowerCase().includes(search.trim().toLowerCase()));
  const publish = async (quiz) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyId(quiz.id);
    setError("");
    setNotice("");
    try {
      await publishQuizToOrganisation(organisation.organisationId, quiz.id);
      setQuizzes((current) => current.filter((item) => item.id !== quiz.id));
      setNotice(`${quiz.quizTitle || quiz.title || quiz.quizCode || "Test"} was added to the Shared Library.`);
      onPublished();
    } catch (publishError) {
      setError(publishError.message || "Could not add this test. Try again.");
    } finally {
      busyRef.current = false;
      setBusyId("");
    }
  };

  return <div className="ol-publish-backdrop" onMouseDown={(event) => {
    if (event.target === event.currentTarget && !busyRef.current) onClose();
  }}>
    <div className="ol-publish-dialog" role="dialog" aria-modal="true" aria-labelledby="ol-publish-title">
      <div className="ol-publish-header"><span className="ol-publish-symbol"><LibraryBig size={23} /></span>
        <button type="button" className="ol-publish-close" onClick={onClose} disabled={Boolean(busyId)} aria-label="Close Add tests dialog"><X size={20} /></button></div>
      <h2 id="ol-publish-title">Add tests</h2>
      <p className="ol-publish-description">Choose tests from My Tests to share with {organisation.name}.</p>
      {notice && <p className="ol-publish-notice" role="status">{notice}</p>}
      {error && <div className="ol-publish-error" role="alert"><span>{error}</span>
        {loading === false && quizzes.length === 0 && <button type="button" onClick={() => setLoadKey((value) => value + 1)}>Retry loading</button>}</div>}
      {!loading && quizzes.length > 0 && <label className="ol-publish-search"><Search size={18} />
        <input type="search" aria-label="Search my tests" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search my tests…" autoFocus /></label>}
      {loading ? <div className="ol-publish-state"><span className="ol-spinner" /><p>Loading your tests…</p></div> :
        error && quizzes.length === 0 ? null : filtered.length ? <div className="ol-publish-list">{filtered.map((quiz) =>
          <div className="ol-publish-row" key={quiz.id}><span className="ol-publish-quiz-icon"><BookOpen size={18} /></span>
            <div className="ol-publish-quiz-info"><strong>{quiz.quizTitle || quiz.title || quiz.quizCode || "Untitled test"}</strong>
              <small>{quiz.subject || "Uncategorized"} · {Array.isArray(quiz.questionIds) ? quiz.questionIds.length : 0} questions</small></div>
            <button type="button" className="ol-publish-add" disabled={Boolean(busyId)} onClick={() => publish(quiz)}>
              {busyId === quiz.id ? "Adding…" : "Add"}</button></div>
        )}</div> : <div className="ol-publish-state"><BookOpen size={28} />
          <strong>{quizzes.length ? "No matching tests" : "All of your eligible tests are already in this library."}</strong>
          {quizzes.length && <p>Try another title, subject, or test code.</p>}</div>}
    </div>
  </div>;
}

export default function OrganisationLibrary({ user }) {
  const navigate = useNavigate();
  const { organisationId, quizId } = useParams();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [search, setSearch] = useState("");
  const [organisation, setOrganisation] = useState(null);
  const [entries, setEntries] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [mediaUrls, setMediaUrls] = useState({});
  const [mediaErrors, setMediaErrors] = useState({});
  const runRef = useRef(0);
  const addRunRef = useRef(0);
  const addBusyRef = useRef(false);
  const pendingAddRef = useRef(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [addedQuizId, setAddedQuizId] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const refreshRunRef = useRef(0);

  useEffect(() => {
    const sync = () => {
      const next = localStorage.getItem("darkMode") === "true";
      setDarkMode(next);
      document.body.classList.toggle("dark-mode", next);
      document.documentElement.setAttribute("data-dark-mode", next ? "true" : "false");
    };
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => { setSearch(""); }, [organisationId, quizId]);

  useEffect(() => {
    addRunRef.current += 1;
    addBusyRef.current = false;
    pendingAddRef.current = null;
    setAdding(false);
    setAddError("");
    setAddedQuizId(null);
  }, [organisationId, quizId]);

  useEffect(() => {
    const run = ++runRef.current;
    setLoading(true);
    setError("");
    setOrganisation(null);
    setEntries([]);
    setPreview(null);
    const load = async () => {
      try {
        const mine = await listMyOrganisations();
        if (run !== runRef.current) return;
        const current = mine.organisations?.find((item) => item.organisationId === organisationId);
        if (!current) {
          setError("Shared Library unavailable for this account.");
          return;
        }
        setOrganisation(current);
        const result = quizId
          ? await previewOrganisationQuiz(organisationId, quizId)
          : await listOrganisationLibrary(organisationId);
        if (run !== runRef.current) return;
        if (quizId) setPreview(result);
        else setEntries(Array.isArray(result.entries) ? result.entries : []);
      } catch (loadError) {
        if (run === runRef.current) setError(loadError.message || "Shared Library is unavailable. Please try again.");
      } finally { if (run === runRef.current) setLoading(false); }
    };
    load();
    return () => { runRef.current += 1; };
  }, [organisationId, quizId, retryKey]);

  useEffect(() => {
    setMediaUrls({});
    setMediaErrors({});
    const content = preview?.learningContent;
    if (!content) return undefined;
    let active = true;
    const urls = [];
    const mediaBlocks = content.blocks.filter((block) => block.type === "image" || block.type === "resource");
    mediaBlocks.forEach(async (block) => {
      try {
        const blob = await loadOrganisationLibraryMedia(organisationId, quizId, block.id, content.revision);
        if (!active) return;
        const url = URL.createObjectURL(blob);
        urls.push(url);
        setMediaUrls((previous) => ({ ...previous, [block.id]: url }));
      } catch (_error) {
        if (active) setMediaErrors((previous) => ({ ...previous, [block.id]: "Media unavailable. Refresh the preview to retry." }));
      }
    });
    return () => { active = false; urls.forEach((url) => URL.revokeObjectURL(url)); };
  }, [organisationId, quizId, preview]);

  const filtered = useMemo(() => entries.filter((entry) =>
    `${entry.title} ${entry.subject} ${entry.publisherName}`.toLowerCase().includes(search.toLowerCase())
  ), [entries, search]);
  const visibleQuestions = useMemo(() => (preview?.questions || []).filter((question) =>
    `${question.questionLabel || ""} ${question.questionText || ""}`.toLowerCase().includes(search.toLowerCase())
  ), [preview, search]);
  const username = accountDisplayName(user);
  const toggleTheme = () => {
    const next = !darkMode;
    localStorage.setItem("darkMode", String(next));
    setDarkMode(next);
    document.body.classList.toggle("dark-mode", next);
    document.documentElement.setAttribute("data-dark-mode", next ? "true" : "false");
  };
  const libraryPath = `/organisations/${encodeURIComponent(organisationId)}/library`;
  const refreshLibrary = async () => {
    const run = runRef.current;
    const refreshRun = ++refreshRunRef.current;
    try {
      const result = await listOrganisationLibrary(organisationId);
      if (run !== runRef.current || refreshRun !== refreshRunRef.current) return;
      setEntries(Array.isArray(result.entries) ? result.entries : []);
      setRefreshError("");
    } catch (_error) {
      if (run === runRef.current && refreshRun === refreshRunRef.current)
        setRefreshError("A test was added, but the library could not refresh. Try refreshing the list.");
    }
  };
  const handleAddToMyQuizzes = async () => {
    if (!preview || !quizId || addBusyRef.current || addedQuizId) return;
    const run = addRunRef.current;
    const key = `studyden:library-add:${user?.uid}:${organisationId}:${quizId}`;
    let requestId = pendingAddRef.current?.key === key ? pendingAddRef.current.requestId : null;
    if (!requestId) {
      try {
        const saved = window.sessionStorage.getItem(key);
        if (saved && /^[a-f0-9]{32}$/.test(saved)) requestId = saved;
      } catch (_error) { /* The in-memory ID still protects retries on this page. */ }
    }
    if (!requestId) {
      try { requestId = newAddRequestId(); }
      catch (_error) {
        setAddError("This browser cannot securely start the copy. Use an updated browser and try again.");
        return;
      }
    }
    pendingAddRef.current = { key, requestId };
    try { window.sessionStorage.setItem(key, requestId); } catch (_error) { /* Optional persistence. */ }
    addBusyRef.current = true;
    setAdding(true);
    setAddError("");
    try {
      const result = await addOrganisationQuizToMine(organisationId, quizId, requestId);
      try { window.sessionStorage.removeItem(key); } catch (_error) { /* Optional persistence. */ }
      if (run !== addRunRef.current) return;
      pendingAddRef.current = null;
      setAddedQuizId(result.quizId);
    } catch (error) {
      if (terminalAddCodes.has(error?.code)) {
        if (run === addRunRef.current) pendingAddRef.current = null;
        try { window.sessionStorage.removeItem(key); } catch (_error) { /* Optional persistence. */ }
      }
      if (run === addRunRef.current) setAddError(addErrorMessage(error));
    } finally {
      if (run === addRunRef.current) {
        addBusyRef.current = false;
        setAdding(false);
      }
    }
  };

  return <div className={`sd-dashboard sd-brand-theme sd-role-teacher ol-page${darkMode ? " sd-dark" : ""}`}>
    <DashboardSidebar role="teacher" username={username} creditsRemaining={user?.creditsRemaining}
      teacherCode={user?.teacherCode} copied={copiedTeacherCode} onCopyCode={async () => {
        if (!user?.teacherCode) return;
        try {
          await navigator.clipboard.writeText(user.teacherCode);
          setCopiedTeacherCode(true);
          window.setTimeout(() => setCopiedTeacherCode(false), 1800);
        } catch (_error) { setError("Could not copy your teacher code."); }
      }} officialDashboardBrand activePath="/organisations"
      open={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={async () => {
        try { await auth.signOut(); navigate("/"); } catch (_error) { setError("Could not sign out."); }
      }} />
    <div className="sd-workspace ol-workspace">
      <DashboardHeader username={username} role="teacher" darkMode={darkMode} onToggleTheme={toggleTheme}
        onOpenMenu={() => setSidebarOpen(true)} search={search} onSearch={setSearch}
        searchPlaceholder={quizId ? "Search questions..." : "Search shared tests..."} />
      <main className="sd-main ol-main">
        <section className="ol-hero"><div><p className="ol-eyebrow">{organisation?.name || "Organisation"}</p><h1>{quizId ? "Test preview" : "Shared Library"}</h1>
          <p>{quizId ? "Preview a shared test and add your own independent copy." : "Tests deliberately shared by teachers in your organisation."}</p></div>
          <span><LibraryBig size={43} /></span>
        </section>
        <button type="button" className="ol-back" onClick={() => navigate(quizId ? libraryPath : `/organisations/${encodeURIComponent(organisationId)}`)}>
          <ArrowLeft size={17} />{quizId ? "Back to Shared Library" : "Back to organisation"}
        </button>
        {error && <div className="ol-error" role="alert"><span>{error}</span><button type="button" onClick={() => setRetryKey((value) => value + 1)}>Retry</button></div>}
        {loading ? <div className="ol-panel ol-state"><span className="ol-spinner" /><p>{quizId ? "Loading test preview…" : "Loading Shared Library…"}</p></div> : error ? null :
          quizId ? preview && <>
            <section className="ol-panel ol-preview-intro"><p className="ol-eyebrow">Teacher preview · Live source</p><h2>{preview.quiz.title}</h2>
              <div className="ol-preview-meta"><span><BookOpen size={16} />{preview.quiz.subject || "Uncategorized"}</span>
                <span><FileQuestion size={16} />{preview.quiz.questionCount} questions</span>
                <span><ShieldCheck size={16} />{preview.quiz.totalMarks} total marks</span></div>
              <p className="ol-preview-description">This preview reflects the owner's current test. Adding it creates an editable copy.</p>
              <div className="ol-add-action">
                <div><strong>Make it yours</strong><p>Add an independent copy of this test, its questions, and its learning resources to My Tests. Your copy will not sync with the source.</p></div>
                <button type="button" className="ol-add-button" disabled={adding || Boolean(addedQuizId)} onClick={handleAddToMyQuizzes}>
                  {adding ? <span className="ol-spinner ol-add-spinner" aria-hidden="true" /> : <CopyPlus size={17} />}
                  {adding ? "Adding to My Tests…" : addedQuizId ? "Added to My Tests" : "Add to My Tests"}
                </button>
              </div>
              {adding && <p className="ol-add-status" role="status">Copying the test and its learning resources. Media may take a little longer.</p>}
              {addError && <p className="ol-add-error" role="alert">{addError}</p>}
              {addedQuizId && <div className="ol-add-success" role="status"><strong>Added to My Tests.</strong>
                <button type="button" onClick={() => navigate(`/edit-quiz/${encodeURIComponent(addedQuizId)}`)}>View my copy <ArrowRight size={16} /></button>
              </div>}
            </section>
            <section className="ol-preview-section"><div className="ol-section-heading"><h2>Questions and answers</h2><span>{preview.questions.length}</span></div>
              {visibleQuestions.length ? visibleQuestions.map((question) => <QuestionPreview key={question.id}
                question={question} number={preview.questions.indexOf(question) + 1}
                organisationId={organisationId} quizId={quizId} />) :
                <div className="ol-panel ol-state">{search ? "No matching questions." : "This test has no questions yet."}</div>}
            </section>
            {preview.learningContent && <section className="ol-panel ol-learning"><div className="ol-section-heading"><h2>Learning Content</h2><span>{preview.learningContent.blocks.length} blocks</span></div>
              <LearningBlocks blocks={preview.learningContent.blocks} mediaUrls={mediaUrls} mediaErrors={mediaErrors} />
            </section>}
          </> : <section className="ol-panel ol-list"><div className="ol-section-heading"><div><p className="ol-eyebrow">Browse</p><h2>Published tests</h2></div>
            <div className="ol-list-actions"><span className="ol-count">{entries.length}</span>
              {user?.role === "teacher" && <button type="button" className="ol-publish-open" onClick={() => setShowAddDialog(true)}><Plus size={17} />Add tests</button>}</div></div>
            {refreshError && <div className="ol-error" role="alert"><span>{refreshError}</span><button type="button" onClick={refreshLibrary}>Retry</button></div>}
            {filtered.length ? <div className="ol-card-grid">{filtered.map((entry) => <article className="ol-card" key={entry.quizId}>
              <span className="ol-card-icon"><BookOpen size={22} /></span><p className="ol-eyebrow">{entry.subject || "Uncategorized"}</p><h3>{entry.title}</h3>
              <div className="ol-card-details"><span><FileQuestion size={15} />{entry.questionCount} questions</span>
                <span><UsersRound size={15} />{entry.publisherName || "Teacher"}</span>
                <span><CalendarDays size={15} />{readableDate(entry.publishedAt)}</span></div>
              <button type="button" className="ol-preview-button" onClick={() => navigate(`${libraryPath}/${encodeURIComponent(entry.quizId)}`)}>Preview test <ArrowRight size={16} /></button>
            </article>)}</div> : <div className="ol-state ol-empty"><BookOpen size={30} /><h3>{search ? "No matching tests" : "Nothing shared yet"}</h3>
              <p>{search ? "Try a different title, subject, or teacher name." : "When teachers publish tests to this organisation, they will appear here."}</p></div>}
          </section>}
      </main>
    </div>
    {showAddDialog && !quizId && organisation && <AddQuizzesDialog organisation={organisation} userUid={user?.uid}
      onClose={() => setShowAddDialog(false)} onPublished={refreshLibrary} />}
  </div>;
}
